import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MenuServiceV2 } from './menu-v2.service';

export interface MenuPhotoItem {
  id: number;
  name: string;
  description: string;
  category: string;
  imageUrl: string | null;
  skipped: boolean;
}

export interface StockPhotoResult {
  id: string;
  url_regular: string;
  url_small: string;
  url_thumb: string;
  alt: string;
  description: string;
}

export interface MenuPhotoCategoryStats {
  category: string;
  total: number;
  withPhoto: number;
}

export interface MenuPhotoStats {
  loaded: boolean;
  /** Items that need a photo (excludes "no photo needed" opt-outs) */
  total: number;
  withPhoto: number;
  skipped: number;
  missing: MenuPhotoItem[];
  percent: number;
  byCategory: MenuPhotoCategoryStats[];
  allItems: MenuPhotoItem[];
}

const EMPTY_STATS: MenuPhotoStats = {
  loaded: false, total: 0, withPhoto: 0, skipped: 0,
  missing: [], percent: 0, byCategory: [], allItems: []
};

/**
 * Photo-completeness layer over the existing menu_management_v2 endpoints.
 * Computes "X of Y menu items have photos" and drives the guided
 * photo-capture queue. Uploads reuse the same image_resized endpoint as
 * the menu editor, so photos added here show up everywhere.
 */
@Injectable({ providedIn: 'root' })
export class MenuPhotosService {

  public stats: BehaviorSubject<MenuPhotoStats> = new BehaviorSubject(EMPTY_STATS);

  /** Demo mode: serves sample data and fakes uploads (used by /menu-photos-demo QA route) */
  demoMode = false;
  private demoItems: MenuPhotoItem[] = [];

  constructor(private http: HttpClient, private menuService: MenuServiceV2) { }

  refresh(storeId: string | number): Observable<MenuPhotoStats> {
    if (this.demoMode) {
      const stats = this.computeStats(this.demoItems, String(storeId));
      this.stats.next(stats);
      return of(stats);
    }
    return this.menuService.getStoreMenuGroups(String(storeId)).pipe(
      switchMap((groups: any) => {
        const menuGroups = (groups as any[]) || [];
        if (!menuGroups.length) {
          return of([] as MenuPhotoItem[]);
        }
        const requests = menuGroups.map(group =>
          this.menuService.getStoreMenuGroupMenuItemGroup(String(storeId), String(group.id), '').pipe(
            map((data: any) => ((data && data.menu_item_groups) || []).map((mig: any) => this.toPhotoItem(mig, group)))
          )
        );
        return forkJoin(requests).pipe(
          map((perGroup: MenuPhotoItem[][]) => {
            // An item group can appear under multiple menu groups — count it once
            const seen = new Map<number, MenuPhotoItem>();
            perGroup.forEach(items => items.forEach(item => {
              if (!seen.has(item.id)) {
                seen.set(item.id, item);
              }
            }));
            return Array.from(seen.values());
          })
        );
      }),
      map(items => this.computeStats(items, String(storeId))),
      tap(stats => this.stats.next(stats))
    );
  }

  uploadPhoto(storeId: string | number, itemGroupId: number, file: File | Blob): Observable<any> {
    if (this.demoMode) {
      const url = URL.createObjectURL(file);
      const item = this.demoItems.find(i => i.id === itemGroupId);
      if (item) {
        item.imageUrl = url;
      }
      return of({ image: url });
    }
    const formData = new FormData();
    formData.append('file', file, 'file');
    return this.http.post(
      `${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_item_group/${itemGroupId}/image_resized/`,
      formData
    );
  }

  /** Search Unsplash stock photos via Apify */
  searchStockPhotos(storeId: string | number, query: string, maxResults = 6): Observable<StockPhotoResult[]> {
    return this.http.post<{ results: StockPhotoResult[] }>(
      `${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_photos/search/`,
      { query, max_results: maxResults }
    ).pipe(map(res => res.results || []));
  }

  /** Apply a stock photo URL to a menu item group */
  applyStockPhoto(storeId: string | number, itemGroupId: number, imageUrl: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_photos/apply/`,
      { menu_item_group_id: itemGroupId, image_url: imageUrl }
    );
  }

  /** "No photo needed" opt-out (drinks, sides…) — kept per store in localStorage */
  setSkipped(storeId: string | number, itemGroupId: number, skipped: boolean) {
    const skips = this.getSkipSet(storeId);
    if (skipped) {
      skips.add(itemGroupId);
    } else {
      skips.delete(itemGroupId);
    }
    localStorage.setItem(this.skipKey(storeId), JSON.stringify(Array.from(skips)));
    const current = this.stats.value;
    if (current.loaded) {
      const items = current.allItems.map(i => i.id === itemGroupId ? { ...i, skipped } : i);
      this.stats.next(this.computeStats(items, String(storeId), true));
    }
  }

  /** Mark an item complete in local stats after a successful upload (no full reload) */
  markPhotoAdded(storeId: string | number, itemGroupId: number, imageUrl: string) {
    const current = this.stats.value;
    if (!current.loaded) { return; }
    const items = current.allItems.map(i => i.id === itemGroupId ? { ...i, imageUrl } : i);
    this.stats.next(this.computeStats(items, String(storeId), true));
  }

  enableDemo() {
    this.demoMode = true;
    if (!this.demoItems.length) {
      this.demoItems = this.buildDemoItems();
    }
  }

  private toPhotoItem(mig: any, group: any): MenuPhotoItem {
    const image = mig && mig.latest_image && mig.latest_image.length ? mig.latest_image[0].image : null;
    return {
      id: mig.id,
      name: mig.name,
      description: mig.description || '',
      category: group.display_name || group.name || 'Menu',
      imageUrl: image || null,
      skipped: false
    };
  }

  private computeStats(items: MenuPhotoItem[], storeId: string, skipsApplied = false): MenuPhotoStats {
    if (!skipsApplied) {
      const skips = this.getSkipSet(storeId);
      items = items.map(i => ({ ...i, skipped: skips.has(i.id) }));
    }
    const eligible = items.filter(i => !i.skipped);
    const withPhoto = eligible.filter(i => !!i.imageUrl);
    const missing = eligible.filter(i => !i.imageUrl);

    const catMap = new Map<string, MenuPhotoCategoryStats>();
    eligible.forEach(item => {
      let cat = catMap.get(item.category);
      if (!cat) {
        cat = { category: item.category, total: 0, withPhoto: 0 };
        catMap.set(item.category, cat);
      }
      cat.total++;
      if (item.imageUrl) {
        cat.withPhoto++;
      }
    });

    return {
      loaded: true,
      total: eligible.length,
      withPhoto: withPhoto.length,
      skipped: items.length - eligible.length,
      missing,
      percent: eligible.length ? Math.round((withPhoto.length / eligible.length) * 100) : 100,
      byCategory: Array.from(catMap.values()).sort((a, b) => (a.total - a.withPhoto) > (b.total - b.withPhoto) ? -1 : 1),
      allItems: items
    };
  }

  private skipKey(storeId: string | number): string {
    return `menu_photos_skip_${storeId}`;
  }

  private getSkipSet(storeId: string | number): Set<number> {
    try {
      const raw = localStorage.getItem(this.skipKey(storeId));
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  }

  private buildDemoItems(): MenuPhotoItem[] {
    const photo = (emoji: string, bg: string) => 'data:image/svg+xml;utf8,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="180"><rect width="240" height="180" fill="${bg}"/><text x="120" y="105" font-size="64" text-anchor="middle">${emoji}</text></svg>`
    );
    let id = 1;
    const item = (name: string, category: string, description: string, imageUrl: string | null): MenuPhotoItem =>
      ({ id: id++, name, description, category, imageUrl, skipped: false });
    return [
      item('Chicken Shawarma Plate', 'Plates', 'Marinated chicken, saffron rice, garlic sauce', photo('🍗', '#FDE68A')),
      item('Beef Kabob Plate', 'Plates', 'Two skewers, grilled tomato, basmati rice', null),
      item('Lamb Gyro Plate', 'Plates', 'Sliced lamb, tzatziki, warm pita', null),
      item('Falafel Plate', 'Plates', 'Crispy falafel, hummus, tahini', photo('🧆', '#D1FAE5')),
      item('Mixed Grill for Two', 'Plates', 'Chicken, beef and lamb with two sides', null),
      item('Chicken Shawarma Wrap', 'Wraps', 'Garlic sauce, pickles, fries inside', photo('🌯', '#FECACA')),
      item('Falafel Wrap', 'Wraps', 'Hummus, tomato, cucumber, tahini', null),
      item('Steak Wrap', 'Wraps', 'Grilled steak, onions, garlic aioli', null),
      item('Hummus', 'Appetizers', 'Classic chickpea dip with olive oil', photo('🫓', '#FEF3C7')),
      item('Baba Ghanoush', 'Appetizers', 'Smoky roasted eggplant dip', null),
      item('Stuffed Grape Leaves', 'Appetizers', 'Six pieces with lemon', null),
      item('Fattoush Salad', 'Salads', 'Crispy pita chips, sumac vinaigrette', null),
      item('Greek Salad', 'Salads', 'Feta, olives, cucumber, tomato', photo('🥗', '#D1FAE5')),
      item('Baklava', 'Desserts', 'Layered phyllo with pistachio and honey', null),
      item('Rice Pudding', 'Desserts', 'Rosewater and cinnamon', null),
      item('Fountain Drink', 'Drinks', 'Coke products, free refills', null),
      item('Mango Lassi', 'Drinks', 'Fresh yogurt smoothie', null),
      item('Mint Lemonade', 'Drinks', 'Fresh squeezed with mint', photo('🍋', '#ECFCCB'))
    ];
  }
}
